import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Package,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  Wind,
  Truck,
  Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { getAdminToken, getAuthHeaders } from '../utils/adminAuth';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login');
      return;
    }
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/orders/${orderId}`, { headers });
      setOrder(response.data);
      setNewStatus(response.data.status);
      setNotes(response.data.notes || '');
    } catch (error) {
      console.error('Failed to fetch order:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = async () => {
    try {
      setUpdating(true);
      const headers = getAuthHeaders();
      await axios.patch(
        `${API_URL}/api/orders/${orderId}`,
        { status: newStatus, notes },
        { headers }
      );
      alert('Order updated successfully!');
      fetchOrder();
    } catch (error) {
      console.error('Failed to update order:', error);
      alert('Failed to update order. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      payment_pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
      paid: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      processing: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
      shipped: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
      delivered: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' }
    };
    const variant = variants[status] || variants.paid;
    return (
      <Badge className={`${variant.bg} ${variant.text} ${variant.border} text-sm px-3 py-1`}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-primary flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
          <Wind className="w-12 h-12 text-gold" />
        </motion.div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-dark-primary flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Order Not Found</h1>
          <Button onClick={() => navigate('/admin/dashboard')} className="bg-gold text-dark-primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-primary">
      {/* Header */}
      <div className="bg-dark-secondary border-b border-gold/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin/dashboard')}
                variant="outline"
                className="border-gold/20 text-gray-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Order Details</h1>
                <p className="text-sm text-gray-400 font-mono">{order.order_number}</p>
              </div>
            </div>
            {getStatusBadge(order.status)}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Order Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information */}
            <Card className="bg-dark-secondary border-gold/10">
              <CardHeader className="border-b border-gold/10">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-gold" />
                  Customer Information
                </h2>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Name</p>
                    <p className="text-white font-medium">{order.customer.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1 flex items-center gap-1">
                      <Mail className="w-4 h-4" /> Email
                    </p>
                    <p className="text-white">{order.customer.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1 flex items-center gap-1">
                      <Phone className="w-4 h-4" /> Phone
                    </p>
                    <p className="text-white">{order.customer.phone}</p>
                  </div>
                  {order.customer.gst_number && (
                    <div>
                      <p className="text-gray-400 text-sm mb-1">GST Number</p>
                      <p className="text-white font-mono">{order.customer.gst_number}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Shipping Address */}
            <Card className="bg-dark-secondary border-gold/10">
              <CardHeader className="border-b border-gold/10">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-gold" />
                  Shipping Address
                </h2>
              </CardHeader>
              <CardContent className="p-6">
                <div className="text-white space-y-1">
                  <p>{order.customer.address.street}</p>
                  <p>{order.customer.address.city}, {order.customer.address.state}</p>
                  <p>{order.customer.address.pincode}</p>
                  <p className="text-gray-400">{order.customer.address.country}</p>
                </div>
              </CardContent>
            </Card>

            {/* Order Items */}
            <Card className="bg-dark-secondary border-gold/10">
              <CardHeader className="border-b border-gold/10">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Package className="w-5 h-5 text-gold" />
                  Order Items
                </h2>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 pb-4 border-b border-gold/10 last:border-0">
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt={item.product_name}
                          className="w-20 h-20 object-cover rounded-lg border border-gold/20"
                        />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium">{item.product_name}</p>
                        <p className="text-gray-400 text-sm">Quantity: {item.quantity}</p>
                      </div>
                      <p className="text-gold font-bold">₹{item.price.toLocaleString('en-IN')}</p>
                    </div>
                  ))}
                  <div className="flex justify-between items-center pt-4 border-t border-gold/20">
                    <span className="text-white font-bold text-lg">Total Amount</span>
                    <span className="text-gold font-bold text-2xl">₹{order.total_amount.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Details */}
            <Card className="bg-dark-secondary border-gold/10">
              <CardHeader className="border-b border-gold/10">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-gold" />
                  Payment Details
                </h2>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Razorpay Order ID</p>
                    <p className="text-white font-mono text-sm">{order.razorpay_order_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Razorpay Payment ID</p>
                    <p className="text-white font-mono text-sm">{order.razorpay_payment_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Currency</p>
                    <p className="text-white">{order.currency}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1 flex items-center gap-1">
                      <Calendar className="w-4 h-4" /> Order Date
                    </p>
                    <p className="text-white">
                      {new Date(order.created_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Update Order */}
          <div className="space-y-6">
            <Card className="bg-dark-secondary border-gold/10 sticky top-6">
              <CardHeader className="border-b border-gold/10">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Truck className="w-5 h-5 text-gold" />
                  Update Order
                </h2>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">Order Status</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full bg-dark-primary border border-gold/20 text-white rounded-lg p-3 focus:outline-none focus:border-gold"
                  >
                    <option value="payment_pending">Payment Pending</option>
                    <option value="paid">Paid</option>
                    <option value="processing">Processing</option>
                    <option value="shipped">Shipped</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="text-gray-300 text-sm mb-2 block">Internal Notes</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this order..."
                    rows={4}
                    className="w-full bg-dark-primary border border-gold/20 text-white rounded-lg p-3 focus:outline-none focus:border-gold resize-none"
                  />
                </div>

                <Button
                  onClick={handleUpdateOrder}
                  disabled={updating || (newStatus === order.status && notes === (order.notes || ''))}
                  className="w-full bg-gold hover:bg-gold-dark text-dark-primary font-bold py-6"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {updating ? 'Updating...' : 'Save Changes'}
                </Button>

                <div className="pt-4 border-t border-gold/10">
                  <p className="text-gray-400 text-xs">
                    Last updated: {new Date(order.updated_at).toLocaleString('en-IN')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderDetails;
